import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { ILoginResult } from '../types/user';
import { authService } from '../services/authService';

interface AuthContextType {
  user: ILoginResult | null;
  login: (username: string, password: string) => Promise<ILoginResult>;
  setupTfa: (tfaCode: string) => Promise<ILoginResult>;
  validateTfa: (tfaCode: string) => Promise<ILoginResult>;
  logout: () => void;
  logoutWithMessage: (message?: string) => void;
  isAuthenticated: boolean;
  hasRole: (roles: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Global reference for logout handler (used by axios interceptor)
let globalLogoutHandler: ((message?: string) => void) | null = null;
let globalNavigateHandler: ((path: string) => void) | null = null;

export const setGlobalLogoutHandler = (handler: ((message?: string) => void) | null) => {
  globalLogoutHandler = handler;
};

export const getGlobalLogoutHandler = (): ((message?: string) => void) | null => {
  return globalLogoutHandler;
};

export const setGlobalNavigateHandler = (handler: ((path: string) => void) | null) => {
  globalNavigateHandler = handler;
};

export const getGlobalNavigateHandler = (): ((path: string) => void) | null => {
  return globalNavigateHandler;
};

interface AuthProviderProps {
  children: ReactNode;
  onSessionExpired?: (message: string) => void;
  navigate?: (path: string) => void;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, onSessionExpired, navigate }) => {
  const [user, setUser] = useState<ILoginResult | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const logout = () => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    if (navigate) {
      navigate('/login');
    } else if (globalNavigateHandler) {
      globalNavigateHandler('/login');
    } else {
      window.location.href = '/login';
    }
  };

  const logoutWithMessage = useCallback((message?: string) => {
    authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    if (message && onSessionExpired) {
      onSessionExpired(message);
      // Show toast briefly then redirect
      setTimeout(() => {
        if (navigate) {
          navigate('/login');
        } else if (globalNavigateHandler) {
          globalNavigateHandler('/login');
        } else {
          window.location.href = '/login';
        }
      }, 1000); // Show toast for 1 second before redirecting
    } else {
      // No message, redirect immediately
      if (navigate) {
        navigate('/login');
      } else if (globalNavigateHandler) {
        globalNavigateHandler('/login');
      } else {
        window.location.href = '/login';
      }
    }
  }, [onSessionExpired, navigate]);

  // Check authentication status on mount and periodically
  useEffect(() => {
    const checkAuth = () => {
      const currentUser = authService.getCurrentUser();
      if (currentUser && authService.isUserLoggedIn()) {
        setUser(currentUser);
        setIsAuthenticated(true);
      } else {
        // Token expired or no user, clear state
        if (currentUser && currentUser.token && authService.isTokenExpired(currentUser.token)) {
          // Token expired, logout
          logoutWithMessage('Your session has expired. Please log in again.');
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      }
    };

    checkAuth();

    // Check authentication status periodically (every 30 seconds)
    const interval = setInterval(checkAuth, 30000);

    return () => clearInterval(interval);
  }, [logoutWithMessage]);

  const login = async (username: string, password: string): Promise<ILoginResult> => {
    const userData = await authService.login(username, password);
    setUser(userData);
    if (!userData.setupTfa && !userData.requestTfaCode) {
      setIsAuthenticated(true);
    }
    return userData;
  };

  const setupTfa = async (tfaCode: string): Promise<ILoginResult> => {
    const userData = await authService.setupTfa(tfaCode);
    setUser(userData);
    if (!userData.setupTfa) {
      setIsAuthenticated(true);
    }
    return userData;
  };

  const validateTfa = async (tfaCode: string): Promise<ILoginResult> => {
    const userData = await authService.validateTfa(tfaCode);
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  };

  const hasRole = (roles: string[]): boolean => {
    return authService.hasRole(roles);
  };

  // Set up global logout handler for axios interceptor
  useEffect(() => {
    setGlobalLogoutHandler(logoutWithMessage);
    if (navigate) {
      setGlobalNavigateHandler(navigate);
    }
    return () => {
      setGlobalLogoutHandler(null);
      setGlobalNavigateHandler(null);
    };
  }, [logoutWithMessage, navigate]);

  return (
    <AuthContext.Provider value={{ user, login, setupTfa, validateTfa, logout, logoutWithMessage, isAuthenticated, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
